import React, { useEffect, useMemo, useState } from "react";
import { MainLayout } from "../../layout/mainLayout";
import { useAppDispatch, useAppSelector } from "../../../framework/customStore";
import { approveVehicleMaintenanceReminderData, deleteVehicleMaintenanceReminderData, fetchVehicleMaintenanceReminderData } from "../../../store/vehicleSlice";
import { useTranslation } from "react-i18next";
import { GridColDef, GridActionsCellItem, GridCellParams } from "@mui/x-data-grid-premium";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import { FieldError, MuiEditableDataGrid } from "../../framework/editableDataGrid/muiEditableDataGrid";
import { MaintenanceReminderStatus, VehicleMaintenanceReminderDto, VehicleMaintenanceReminderQueryParameters } from "../../../models/vehicle/vehicleMaintenanceReminder";
import { Button, Dialog, DialogActions, DialogTitle } from "@mui/material";
import { saveVehicleMaintenanceReminder } from "../../../services/vehicleMaintenanceReminderService";
import { showSuccessMessage } from "../../../models/store/storeActions";

export const MaintenanceReminderView = () => (
    <MainLayout>
        <MaintenanceReminderViewContent />
    </MainLayout>
);

export const MaintenanceReminderViewContent = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const {
        vehicleMaintenanceRemindersLoading,
        vehicleMaintenanceReminders
    } = useAppSelector((s) => s.vehicle);

    const [editId, setEditId] = useState<string | undefined>();
    const [rows, setRows] = useState<VehicleMaintenanceReminderDto[]>([]);
    const [deleteRow, setDeleteRow] = useState<VehicleMaintenanceReminderDto | null>(null);
    const [approveRow, setApproveRow] = useState<VehicleMaintenanceReminderDto | null>(null);

    const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);

    const queryParams: VehicleMaintenanceReminderQueryParameters = {
        states: null,
    };

    /* Load data */
    useEffect(() => {
        dispatch(fetchVehicleMaintenanceReminderData(queryParams) as any);
    }, []);

    useEffect(() => {
        if (!vehicleMaintenanceRemindersLoading) {
            setRows(() => [...vehicleMaintenanceReminders]);
        }
    }, [vehicleMaintenanceReminders, vehicleMaintenanceRemindersLoading]);

    const validateRow = (
        newRowData: VehicleMaintenanceReminderDto
    ): FieldError[] => {
        const errors: FieldError[] = [];

        if (newRowData.remindByDate && !newRowData.datesBeforeReminderStarts) {
            errors.push({ field: "datesBeforeReminderStarts", error: t("validation.fieldIsRequired", {
                field: t("vehicleMaintenanceReminder.datesBeforeReminderStarts"),
            }) });
        }
        if (newRowData.remindByOdometerReading && !newRowData.distanceBeforeReminderStarts) {
            errors.push({ field: "odometerReadingForMaintenance", error: t("validation.fieldIsRequired", {
                field: t("vehicleMaintenanceReminder.distanceBeforeReminderStarts"),
            }) });
        }
        if (newRowData.remindByOdometerReading && !newRowData.odometerReadingForMaintenance) {
            errors.push({ field: "odometerReadingForMaintenance", error: t("validation.fieldIsRequired", {
                field: t("vehicleMaintenanceReminder.odometerReadingForMaintenance"),
            }) });
        }

        return errors;
    };

    const getStatusText = (status?: MaintenanceReminderStatus) =>
        status ? t(`vehicleMaintenanceReminder.status.${MaintenanceReminderStatus[status]}`) : "";

    const isCellEditable = (params: GridCellParams) => {
        const maintenanceFields = [
            "remindByDate",
            "remindByOdometerReading",
            "datesBeforeReminderStarts",
            "odometerReadingForMaintenance",
            "distanceBeforeReminderStarts",
        ];

        if (!maintenanceFields.includes(params.field)) return false;

        const row = params.row as VehicleMaintenanceReminderDto;
        if (row.status === MaintenanceReminderStatus.Completed) return false;

        return true;
    };

    /* Column definitions */
    const columns: GridColDef<VehicleMaintenanceReminderDto>[] = useMemo(
        () => [
            {
                field: "status",
                headerName: t("vehicleMaintenance.maintenanceStatus"),
                editable: false,
                type: "singleSelect",
                flex: 1.5,
                valueOptions: Object.values(MaintenanceReminderStatus)?.map((v) => ({
                    value: v,
                    label: getStatusText(v),
                }))
            },
            {
                field: "vehicleId",
                headerName: t("vehicle.vehicle"),
                flex: 1,
                editable: false,
            },
            {
                field: "remindByDate",
                headerName: t("vehicleMaintenanceReminder.remindByDate"),
                editable: true,
                flex: 1.5,
                type: "singleSelect",
                valueOptions: [
                    { value: false, label: t("common.notInUse") },
                    { value: true, label: t("common.inUse") }
                ]
            },
            {
                field: "remindByOdometerReading",
                headerName: t("vehicleMaintenanceReminder.remindByOdometerReading"),
                editable: true,
                flex: 1.5,
                type: "singleSelect",
                valueOptions: [
                    { value: false, label: t("common.notInUse") },
                    { value: true, label: t("common.inUse") }
                ]
            },
            {
                field: "plannedMaintenanceDateTime",
                headerName: t("vehicleMaintenance.plannedMaintenanceDateTime"),
                editable: false,
                flex: 1.5,
            },
            {
                field: "datesBeforeReminderStarts",
                headerName: t("vehicleMaintenanceReminder.datesBeforeReminderStarts"),
                editable: true,
                type: "number",
                flex: 1.5
            },
            {
                field: "odometerReadingForMaintenance",
                headerName: t("vehicleMaintenanceReminder.odometerReadingForMaintenance"),
                editable: true,
                type: "number",
                flex: 1.5
            },
            {
                field: "distanceBeforeReminderStarts",
                headerName: t("vehicleMaintenanceReminder.distanceBeforeReminderStarts"),
                editable: true,
                type: "number",
                flex: 1.5,
            },
            {
                field: "actions",
                type: "actions",
                getActions: (params) => {
                    const actions = [];
                    if (params.row.status === MaintenanceReminderStatus.AwaitingApproval) {
                        actions.push(
                            <GridActionsCellItem
                                key={`approve-${params.id}`}
                                color="success"
                                icon={<CheckCircleIcon />}
                                label={t("vehicleMaintenanceReminder.approve")}
                                onClick={() => setApproveRow(params.row)}
                            />
                        );
                    }
                    actions.push(
                        <GridActionsCellItem
                            key={`delete-${params.id}`}
                            color="error"
                            icon={<DeleteIcon />}
                            label={t("common.remove")}
                            onClick={() => setDeleteRow(params.row)}
                        />
                    );
                    return actions;
                },
            },
        ], [rows]);

    const handleSubmit = async(values: VehicleMaintenanceReminderDto) => {
        return new Promise<VehicleMaintenanceReminderDto>((resolve, reject) => {
            const isNew = values?.id === null;

            const payload: VehicleMaintenanceReminderDto = {
                ...values
            };

            if (!isNew) {
                saveVehicleMaintenanceReminder(payload)
                    .then((res) => {
                        setHighlightedRowId(res?.data.id);
                        setTimeout(() => setHighlightedRowId(null), 2000);

                        const message = isNew ? t("vehicleMaintenanceReminder.maintenanceAddedSuccessfully") : t("vehicleMaintenanceReminder.maintenanceEditedSuccessfully");
                        dispatch(showSuccessMessage(message));
                        resolve(res.data);
                    })
                    .catch((error) => {
                        const message = isNew ? t("vehicleMaintenanceReminder.saveFailed") : t("vehicleMaintenanceReminder.editFailed");
                        console.error("Error saving vehicle maintenance: ", error);
                        reject(new Error(message));
                    });
            }
        });
    };

    return (
        <>
            <MuiEditableDataGrid<Partial<VehicleMaintenanceReminderDto>>
                title={t("vehicleMaintenanceReminder.listTitle")}
                editId={editId}
                setEditId={setEditId}
                persistStateKey="EditableMaintenanceReminderTableState_V3"
                columns={columns}
                rows={rows}
                setRows={setRows}
                handleAddRow={null}
                handleSubmit={handleSubmit}
                validateRow={validateRow}
                isCellEditable={isCellEditable}
                pagination
                highlightedRowId={highlightedRowId}
                disableAddRowButton={true}
                disableColumnSelector={true}
            />

            <Dialog open={!!approveRow} onClose={() => setApproveRow(null)}>
                <DialogTitle>{t("vehicleMaintenanceReminder.confirmApprove")}</DialogTitle>
                <DialogActions>
                    <Button onClick={() => setApproveRow(null)}>
                        {t("common.cancel")}
                    </Button>
                    <Button
                        color="success"
                        onClick={() => {
                            dispatch(
                                approveVehicleMaintenanceReminderData({ vehicleMaintenanceId: approveRow.vehicleMaintenanceId }) as any
                            ).then(() => {
                                dispatch(showSuccessMessage(t("vehicleMaintenanceReminder.approveSuccess")));
                            });
                            setApproveRow(null);
                        }}
                    >
                        {t("vehicleMaintenanceReminder.approve")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!deleteRow} onClose={() => setDeleteRow(null)}>
                <DialogTitle>{t("vehicleMaintenanceReminder.confirmDelete")}</DialogTitle>
                <DialogActions>
                    <Button onClick={() => setDeleteRow(null)}>
                        {t("common.cancel")}
                    </Button>
                    <Button
                        color="error"
                        onClick={() => {
                            dispatch(
                                deleteVehicleMaintenanceReminderData({ vehicleMaintenanceReminderId: deleteRow.id }) as any
                            );
                            setRows((r) => r.filter((x) => x.id !== deleteRow.id));
                            setDeleteRow(null);
                        }}
                    >
                        {t("common.remove")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
